import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Users } from "lucide-react";
import { pct, currency, statusColors } from "./KPICard";

export function AgentLeaderboard({ title, agents, type, benchmarks = {} }) {
  const [statusFilter, setStatusFilter] = useState("all");

  if (!agents || agents.length === 0) {
    return (
      <Card className="glass border-white/10">
        <CardHeader>
          <CardTitle className="text-lg text-slate-100">{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-400">No agent data available for this period.</p>
        </CardContent>
      </Card>
    );
  }

  const agentsWithStatus = agents.map((agent) => {
    const benchmarkKey = type === "booking" ? "lead_to_booked_rate" : "sales_conversion_rate";
    const targetBenchmark = benchmarks[benchmarkKey];
    const actualValue = type === "booking" ? agent.booking_rate : agent.sales_conversion_rate;
    
    let benchmarkStatus = "none";
    if (targetBenchmark?.target && actualValue !== undefined) {
      const variance = (actualValue - targetBenchmark.target) / targetBenchmark.target;
      if (actualValue >= targetBenchmark.target) {
        benchmarkStatus = "green";
      } else if (Math.abs(variance) <= 0.20) {
        benchmarkStatus = "yellow";
      } else {
        benchmarkStatus = "red";
      }
    }
    return { ...agent, benchmarkStatus };
  });

  const filteredAgents = statusFilter === "all" 
    ? agentsWithStatus 
    : statusFilter === "yellow" 
    ? agentsWithStatus.filter(a => a.benchmarkStatus === "yellow" || a.benchmarkStatus === "red")
    : agentsWithStatus.filter(a => a.benchmarkStatus === statusFilter);

  const targetBenchmark = benchmarks[type === "booking" ? "lead_to_booked_rate" : "sales_conversion_rate"];

  return (
    <Card className="glass border-white/10" data-testid={`${type}-agents-table`}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg text-slate-100 flex items-center gap-2">
            <Users className="h-5 w-5 text-indigo-400" />
            {title}
            {targetBenchmark?.target && (
              <span className="text-xs text-slate-400 font-normal ml-2">
                (Target: {pct(targetBenchmark.target)})
              </span>
            )}
          </CardTitle>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-white/5 border border-white/10 rounded-md px-2 py-1 text-xs text-slate-300"
            data-testid="agent-status-filter"
          >
            <option value="all">All Status</option>
            <option value="red">Critical Only</option>
            <option value="yellow">Warning & Critical</option>
            <option value="green">On Target</option>
          </select>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow className="border-white/10">
              <TableHead className="text-slate-300">Agent</TableHead>
              {type === "booking" ? (
                <>
                  <TableHead className="text-slate-300 text-right">Leads</TableHead>
                  <TableHead className="text-slate-300 text-right">Contact %</TableHead>
                  <TableHead className="text-slate-300 text-right">Book %</TableHead>
                  <TableHead className="text-slate-300 text-right">Rev/Lead</TableHead>
                </>
              ) : (
                <>
                  <TableHead className="text-slate-300 text-right">Appts Held</TableHead>
                  <TableHead className="text-slate-300 text-right">Conv %</TableHead>
                  <TableHead className="text-slate-300 text-right">Avg Premium</TableHead>
                  <TableHead className="text-slate-300 text-right">Rev/Appt</TableHead>
                </>
              )}
              <TableHead className="text-slate-300 text-right">Benchmark</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredAgents.slice(0, 10).map((agent, idx) => (
              <TableRow key={agent.agent_id} className="border-white/10 hover:bg-white/5">
                <TableCell className="font-medium text-slate-100">
                  <div className="flex items-center gap-2">
                    {idx < 3 && (
                      <Badge className={`text-xs ${
                        idx === 0 ? "bg-yellow-500/20 text-yellow-300" :
                        idx === 1 ? "bg-slate-400/20 text-slate-300" :
                        "bg-orange-500/20 text-orange-300"
                      }`}>
                        #{idx + 1}
                      </Badge>
                    )}
                    {agent.agent_name}
                  </div>
                </TableCell>
                {type === "booking" ? (
                  <>
                    <TableCell className="text-right font-mono text-slate-100">{agent.leads_assigned}</TableCell>
                    <TableCell className="text-right font-mono text-slate-100">{pct(agent.contact_rate)}</TableCell>
                    <TableCell className={`text-right font-mono ${
                      agent.benchmarkStatus === "green" ? "text-emerald-400" :
                      agent.benchmarkStatus === "yellow" ? "text-amber-400" :
                      agent.benchmarkStatus === "red" ? "text-red-400" :
                      "text-slate-100"
                    }`}>{pct(agent.booking_rate)}</TableCell>
                    <TableCell className="text-right font-mono text-emerald-400">{currency(agent.revenue_per_lead)}</TableCell>
                  </>
                ) : (
                  <>
                    <TableCell className="text-right font-mono text-slate-100">{agent.appointments_held}</TableCell>
                    <TableCell className={`text-right font-mono ${
                      agent.benchmarkStatus === "green" ? "text-emerald-400" :
                      agent.benchmarkStatus === "yellow" ? "text-amber-400" :
                      agent.benchmarkStatus === "red" ? "text-red-400" :
                      "text-slate-100"
                    }`}>{pct(agent.sales_conversion_rate)}</TableCell>
                    <TableCell className="text-right font-mono text-slate-100">{currency(agent.avg_premium)}</TableCell>
                    <TableCell className="text-right font-mono text-emerald-400">{currency(agent.revenue_per_appointment)}</TableCell>
                  </>
                )}
                <TableCell className="text-right">
                  <Badge className={`text-xs border ${statusColors[agent.benchmarkStatus] || statusColors.none}`}>
                    {agent.benchmarkStatus === "green" && "✓"}
                    {agent.benchmarkStatus === "yellow" && "⚠"}
                    {agent.benchmarkStatus === "red" && "✗"}
                    {agent.benchmarkStatus === "none" && "-"}
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {filteredAgents.length === 0 && (
          <p className="text-sm text-slate-400 mt-4 text-center">No agents match the selected filter.</p>
        )}
      </CardContent>
    </Card>
  );
}
